import React, {useState} from 'react';
import {
  IconButton,
  Badge,
  Popover,
  Box,
  Typography,
  List,
  ListItemButton,
  ListItemText,
  Divider,
  CircularProgress,
  Button,
} from '@mui/material';
import NotificationsIcon from '@mui/icons-material/Notifications';
import {useQuery, useMutation} from '@apollo/client';
import {GET_MY_NOTIFICATIONS, GET_MY_UNREAD_NOTIFICATION_COUNT} from '../graphql/queries';
import {MARK_NOTIFICATION_READ} from '../graphql/mutations';

export const NotificationBell: React.FC = () => {
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const {data: countData} = useQuery(GET_MY_UNREAD_NOTIFICATION_COUNT, {
    pollInterval: 30000,
  });

  const {data: notifData, loading, refetch} = useQuery(GET_MY_NOTIFICATIONS, {
    variables: {limit: 20},
    skip: !open,
    fetchPolicy: 'network-only',
  });

  const [markRead] = useMutation(MARK_NOTIFICATION_READ);

  const unread = countData?.myUnreadNotificationCount ?? 0;
  const notifications = notifData?.myNotifications ?? [];

  const handleOpen = (e: React.MouseEvent<HTMLElement>) => {
    setAnchorEl(e.currentTarget);
  };

  const handleClose = () => setAnchorEl(null);

  const handleMarkRead = async (id: string) => {
    await markRead({variables: {notificationId: id}});
    refetch();
  };

  return (
    <>
      <IconButton color="inherit" onClick={handleOpen} size="small">
        <Badge badgeContent={unread} color="error" max={99}>
          <NotificationsIcon />
        </Badge>
      </IconButton>

      <Popover
        open={open}
        anchorEl={anchorEl}
        onClose={handleClose}
        anchorOrigin={{vertical: 'bottom', horizontal: 'right'}}
        transformOrigin={{vertical: 'top', horizontal: 'right'}}
        slotProps={{paper: {sx: {width: 360, maxHeight: 480}}}}
      >
        <Box sx={{px: 2, py: 1.5, display: 'flex', alignItems: 'center'}}>
          <Typography variant="subtitle1" fontWeight={600} sx={{flex: 1}}>
            Notifications
          </Typography>
          {unread > 0 && (
            <Typography variant="caption" color="primary">
              {unread} unread
            </Typography>
          )}
        </Box>
        <Divider />

        {loading ? (
          <Box sx={{display: 'flex', justifyContent: 'center', py: 4}}>
            <CircularProgress size={24} />
          </Box>
        ) : notifications.length === 0 ? (
          <Box sx={{py: 4, textAlign: 'center'}}>
            <Typography variant="body2" color="text.secondary">
              No notifications yet
            </Typography>
          </Box>
        ) : (
          <List dense disablePadding sx={{overflow: 'auto', maxHeight: 380}}>
            {notifications.map((n: any) => (
              <ListItemButton
                key={n.id}
                onClick={() => !n.read && handleMarkRead(n.id)}
                sx={{
                  bgcolor: n.read ? 'transparent' : 'action.hover',
                  borderLeft: n.read ? 'none' : '3px solid',
                  borderLeftColor: 'primary.main',
                }}
              >
                <ListItemText
                  primary={
                    <Typography
                      variant="body2"
                      fontWeight={n.read ? 400 : 600}
                    >
                      {n.title}
                    </Typography>
                  }
                  secondary={
                    <>
                      <Typography variant="caption" component="span">
                        {n.body}
                      </Typography>
                      <br />
                      <Typography
                        variant="caption"
                        color="text.disabled"
                        component="span"
                      >
                        {new Date(n.createdAt).toLocaleString()}
                      </Typography>
                    </>
                  }
                />
              </ListItemButton>
            ))}
          </List>
        )}
      </Popover>
    </>
  );
};
